export interface MatrixLike
{
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

export interface RegionLike
{
  x: number;
  y: number;
  width: number;
  height: number;
}

export class Point
{
  constructor(public x: number, public y: number)
  { }

  public add(other: Point): Point
  {
    return new Point(this.x + other.x, this.y + other.y);
  }
}

export function clamp(x: number, low: number, high: number): number
{
  return (
      x < low
        ? low
        : x > high
            ? high
            : x
  );
}

export function transformPoint(matrix: MatrixLike, point: Point): Point
{
  return new Point(
      matrix.a * point.x + matrix.c * point.y + matrix.e,
      matrix.b * point.x + matrix.d * point.y + matrix.f);
}

function dot(a: Point, b: Point): number
{
  return a.x * b.x + a.y * b.y;
}

interface HalfPlane
{
  normal: Point;
  limit: number;
}

export class Region
{
  public static empty(): Region
  {
    return Region.fromSimilar({ x: 0, y: 0, width: 0, height: 0, });
  }

  public static fromSimilar(description: RegionLike): Region
  {
    return new Region(
        description.x, description.y, description.width, description.height);
  }

  public static fromTwoOrderedPoints(topLeft: Point, bottomRight: Point): Region
  {
    return new Region(
        topLeft.x,
        topLeft.y,
        bottomRight.x - topLeft.x,
        bottomRight.y - topLeft.y);
  }

  public static fromTwoPoints(pointA: Point, pointB: Point): Region
  {
    return Region.fromTwoOrderedPoints(
        new Point(
            Math.min(pointA.x, pointB.x),
            Math.min(pointA.y, pointB.y)),

        new Point(
            Math.max(pointA.x, pointB.x),
            Math.max(pointA.y, pointB.y)));
  }

  public static centeredFit(container: Region, aspectRatio: number): Region
  {
    const width = Math.min(container.width, container.height * aspectRatio);
    const height = width / aspectRatio;

    return new Region(
        container.x + (container.width - width) / 2,
        container.y + (container.height - height) / 2,
        width,
        height);
  }

  public static interpolate(
      from: Region, to: Region, progress: number): Region
  {
    const mix = (a: number, b: number): number => a + (b - a) * progress;

    return new Region(
        mix(from.x, to.x),
        mix(from.y, to.y),
        mix(from.width, to.width),
        mix(from.height, to.height));
  }

  constructor(
      public x: number,
      public y: number,
      public width: number,
      public height: number)
  {}

  public get topLeft(): Point
  {
    return new Point(this.x, this.y);
  }

  public get bottomRight(): Point
  {
    return new Point(this.x + this.width, this.y + this.height);
  }

  public get center(): Point
  {
    return new Point(this.x + this.width / 2, this.y + this.height / 2);
  }

  public equals(other: RegionLike): boolean
  {
    return other !== undefined
        && this.x === other.x
        && this.y === other.y
        && this.width === other.width
        && this.height === other.height;
  }

  public get corners(): Point[]
  {
    return [
        this.topLeft,
        new Point(this.bottomRight.x, this.y),
        this.bottomRight,
        new Point(this.x, this.bottomRight.y),
    ];
  }

  public transformedBy(matrix: MatrixLike): Region
  {
    const corners =
        this.corners.map((corner) => transformPoint(matrix, corner));

    const xs = corners.map((corner) => corner.x);
    const ys = corners.map((corner) => corner.y);

    return Region.fromTwoOrderedPoints(
        new Point(Math.min(...xs), Math.min(...ys)),
        new Point(Math.max(...xs), Math.max(...ys)));
  }

  public intersectedWith(other: RegionLike): Region
  {
    const left = Math.max(this.x, other.x);
    const top = Math.max(this.y, other.y);
    const right = Math.min(this.x + this.width, other.x + other.width);
    const bottom = Math.min(this.y + this.height, other.y + other.height);

    return new Region(
        left, top, Math.max(0, right - left), Math.max(0, bottom - top));
  }

  public centeredAt(center: Point): Region
  {
    return new Region(
        center.x - this.width / 2,
        center.y - this.height / 2,
        this.width,
        this.height);
  }
}

export class ConvexArea
{
  public static fromRegion(region: Region): ConvexArea
  {
    return ConvexArea.fromPolygon(region.corners);
  }

  public static fromPolygon(corners: Point[]): ConvexArea
  {
    const inside = new Point(
        corners.reduce((sum, corner) => sum + corner.x, 0) / corners.length,
        corners.reduce((sum, corner) => sum + corner.y, 0) / corners.length);

    return new ConvexArea(
        corners.map((corner, index) => {
          const next = corners[(index + 1) % corners.length];
          const normal =
              new Point(next.y - corner.y, corner.x - next.x);

          return dot(normal, inside) <= dot(normal, corner)
              ? {normal, limit: dot(normal, corner)}
              : {normal: new Point(-normal.x, -normal.y),
                 limit: -dot(normal, corner)};
        }));
  }

  private constructor(private readonly halfPlanes: HalfPlane[])
  {}

  public maxRectangleFactor(origin: Point, diagonal: Point): number
  {
    const corners = [
        new Point(diagonal.x, 0),
        new Point(0, diagonal.y),
        diagonal,
    ];

    let factor = Infinity;

    for (const plane of this.halfPlanes) {
      const room = plane.limit - dot(plane.normal, origin);

      for (const corner of corners) {
        const towards = dot(plane.normal, corner);

        if (towards > 0) {
          factor = Math.min(factor, room / towards);
        }
      }
    }

    return factor;
  }
}
